import React, { useEffect, useRef, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";

// A placeholder implementation for the dashboard preview
// In a real implementation, you would use actual screenshots

const carouselData = [

  {
    title: "Dashboard",
    imageUrl: "images/join-page/feature-dashboard.png",
    originalWidth: 1162,
    originalHeight: 1027,
    annotations: [
      {
        id: "annotation-1747000196439",
        x: 386.5,
        y: 230,
        text: "Easily access your latest progress",
        color: "#8B5CF6"
      },
      {
        id: "annotation-1747000207241",
        x: 106.5,
        y: 521,
        text: "See how your product testing is going",
        color: "#8B5CF6"
      }
    ],
  },
  {
    title: "Tastermonials",
    imageUrl: "images/join-page/feature-tastermonials.png",
    originalWidth: 1170,
    originalHeight: 1010,
    annotations: [
      {
        id: "annotation-1746999378127",
        x: 207.5,
        y: 498,
        text: "Easily find and use reviews in your own marketing",
        color: "#8B5CF6",
      },
      {
        id: "annotation-1746999363415",
        x: 212.5,
        y: 808,
        text: "Non-influencer tester may choose to leave anonymous reviews",
        color: "#8B5CF6",
      },
    ],
  },
  {
    title: "Participant Details",
    imageUrl: "images/join-page/feature-participants.png",
    originalWidth: 1161,
    originalHeight: 751,
    annotations: [
      {
        id: "annotation-1746999292446",
        x: 309.5,
        y: 126,
        text: "Detailed information about the demographics of the participants",
        color: "#8B5CF6",
      },
    ],
  },
  {
    title: "Testimonial pricing",
    imageUrl: "images/join-page/feature-tastermonials-pricing.png",
    originalWidth: 1161,
    originalHeight: 803,
    annotations: [
      {
        id: "annotation-1746998592423",
        x: 298.5,
        y: 211,
        text: "Adjust your budget any time",
        color: "#8B5CF6",
      },
      {
        id: "annotation-1746998607328",
        x: 213.5,
        y: 388,
        text: "You are in control where your budget is spent",
        color: "#8B5CF6",
      },
    ],
  },
  {
    title: "Pay as you go pricing",
    imageUrl: "images/join-page/feature-pay-as-you-go.png",
    originalWidth: 1161,
    originalHeight: 987,
    annotations: [
      {
        id: "annotation-1746994966324",
        x: 291,
        y: 76,
        text: "You decide how much budget you need. Only pay for what you use when you use it.",
        color: "#8B5CF6",
      },
      {
        id: "annotation-1746995142917",
        x: 254,
        y: 619,
        text: "Easily calculate how much you may need to add or use our pre-defined packages.",
        color: "#8B5CF6",
      },
      {
        id: "annotation-1746995180933",
        x: 206,
        y: 322,
        text: "We have a transparent pricing and you will always see what your credits are used for.",
        color: "#8B5CF6",
      },
    ],
  },
  {
    title: "Add-on Services & Transparent billing",
    imageUrl: "images/join-page/feature-add-ons.png",
    originalWidth: 1158,
    originalHeight: 864,
    annotations: [
      {
        id: "annotation-1746995475458",
        x: 243,
        y: 18,
        text: "Add on services enables you to reach further users",
        color: "#8B5CF6",
      },
      {
        id: "annotation-1746995489255",
        x: 292,
        y: 330,
        text: "With our billing history, you gain a clear breakdown of where your credits have been spent",
        color: "#8B5CF6",
      },
    ],
  },
];

const DashboardPreview: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements =
              entry.target.querySelectorAll(".animate-on-scroll");
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add("visible");
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-rotate carousel every 10s
  useEffect(() => {
    let interval: NodeJS.Timeout;

    const startAutoRotate = () => {
      interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % carouselData.length);
      }, 10000);
    };

    startAutoRotate();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [activeIndex]);

  // Sync carousel when activeIndex changes
  useEffect(() => {
    if (carouselApi) {
      carouselApi.scrollTo(activeIndex);
    }
  }, [activeIndex, carouselApi]);

  // Sync activeIndex when carousel is changed by user interaction
  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      const idx = carouselApi.selectedScrollSnap();
      setActiveIndex(idx);
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  // Handler for manual navigation
  const handleSelect = (idx: number) => setActiveIndex(idx);

  return (
    <section
      ref={sectionRef}
      id="dashboard"
      className="section-padding bg-neutral-50"
    >
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Your Marketing Command Center
          </h2>
          <p className="text-neutral-700 text-lg">
            Tastermonial's dashboard gives you powerful tools to manage and
            leverage your verified testimonials.
          </p>
        </div>
        <div className="relative mb-8 animate-on-scroll">
          <Carousel opts={{ loop: true }} setApi={setCarouselApi}>
            <CarouselContent>
              {carouselData.map((screen, idx) => (
                <CarouselItem key={screen.title} className="max-h-[60vh]">
                  <div className="h-full bg-white rounded-lg border border-neutral-200 shadow-md overflow-hidden relative flex items-center justify-center pt-12">
                    {/* Window bar overlay */}
                    <div
                      className="absolute top-0 left-0 w-full h-12 flex items-center px-6"
                      style={{ background: "#7650CC", zIndex: 20 }}
                    >
                      <span className="text-white font-bold text-base">
                        Tastermonial Dashboard
                      </span>
                      <div className="ml-auto flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-white/20" />
                        <div className="w-7 h-7 rounded-full bg-white/20" />
                      </div>
                    </div>
                    <div className="h-full flex items-center justify-center">
                      <div className="h-full w-full relative">
                        <img
                          ref={(el) => (imageRefs.current[idx] = el)}
                          src={
                            screen.imageUrl.startsWith("/")
                              ? screen.imageUrl
                              : `/${screen.imageUrl}`
                          }
                          alt={screen.title}
                          className="h-full w-auto max-w-full object-contain mx-auto"
                        />
                        {/* Annotations */}
                        {screen.annotations.map((ann, i) => {
                          // Convert pixel coordinates to percentages
                          const xPercent = (ann.x / screen.originalWidth) * 100;
                          const yPercent = (ann.y / screen.originalHeight) * 100;

                          return (
                            <div
                              key={ann.id}
                              className="absolute"
                              style={{
                                top: `${yPercent}%`,
                                left: `${xPercent}%`,
                                transform: 'translate(-50%, -50%)',
                                zIndex: 30,
                              }}
                            >
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs animate-pulse relative"
                                style={{ background: ann.color, width: 24, height: 24 }}
                              >
                                <div className="absolute inset-0 rounded-full animate-ping" style={{ background: ann.color, opacity: 0.3 }} />
                                {i + 1}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="absolute inset-y-0 -left-12 flex items-center z-40">
              <CarouselPrevious
                className="static translate-y-0 translate-x-0"
                onClick={() =>
                  handleSelect(
                    (activeIndex - 1 + carouselData.length) %
                      carouselData.length
                  )
                }
              />
            </div>
            <div className="absolute inset-y-0 -right-12 flex items-center z-40">
              <CarouselNext
                className="static translate-y-0 translate-x-0"
                onClick={() =>
                  handleSelect((activeIndex + 1) % carouselData.length)
                }
              />
            </div>
          </Carousel>
          {/* Annotation List */}
          <div className="mt-8 px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {carouselData[activeIndex].annotations.map((ann, i) => (
                <div key={ann.id} className="flex items-start gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs"
                    style={{ background: ann.color }}
                  >
                    {i + 1}
                  </div>
                  <p className="text-sm text-neutral-700">{ann.text}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Dots for manual navigation */}
          <div className="flex justify-center mt-4 gap-2">
            {carouselData.map((_, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === activeIndex ? "bg-primary w-6" : "bg-neutral-300"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
